<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore.ts';
import { useI18n } from 'vue-i18n';
import {
  type RouteRecordRaw,
  useRoute
} from 'vue-router';

import AvatarComponent from '@/components/ui/AvatarComponent.vue';
import AppLogoComponent from '@/components/ui/AppLogoComponent.vue';
import { getRoutes } from '@/router';

import type { MeResponse } from '@/types/services/auth/auth.type.ts';

const authStore = useAuthStore()
const route = useRoute()
const { t } = useI18n()

const drawer = ref(true)
const openDetails = ref<Record<string, boolean>>({})

const user = computed(() => authStore.user as MeResponse)
const userFullName = computed(() => `${user.value?.firstName} ${user.value?.lastName}`.trim())

// --- Navigation items --- //
const navigationItems = computed(() => {
  const routes = getRoutes().filter(route => {
    const hasPermissionFn = authStore.hasPermission as ((perm: string[] | undefined) => boolean) | undefined

    return route.meta?.inNav &&
      (route.meta.permissions === undefined ||
      hasPermissionFn?.(route.meta.permissions as string[] | undefined)
    );
  })

  return routes.reduce((acc, route) => {
    const parent = route.meta?.parent as string
    if (!acc[parent]) {
      acc[parent] = {}
    }
    acc[parent][route.name as string] = route
    return acc
  }, {} as Record<string, Record<string, RouteRecordRaw>>)
})

const logout = async (): Promise<void> => {
  await authStore.logout()
}

const toggleDrawer = (): void => {
  drawer.value = !drawer.value
  setTimeout(() => window.dispatchEvent(new Event('resize')), 200)
}

const isActive = (navRoute: RouteRecordRaw): boolean => {
  return route.meta?.related === navRoute.meta?.related
}

const isParentActive = (parent: string): boolean => {
  return route.meta?.parent === parent
}

const toggleDetails = (event: Event, parent: string): void => {
  openDetails.value[parent] = (event.target as HTMLDetailsElement).open
}

const isDetailsOpen = (parent: string): boolean => {
  return openDetails.value[parent]
}

defineExpose({
  toggleDrawer
})
</script>

<template>
  <v-navigation-drawer v-model="drawer" class="border-0" width="235" color="#fdfaf3">
    <!-- Logo -->
    <template #prepend>
      <AppLogoComponent width="50" />
    </template>

    <!-- Navigation items -->
    <div class="px-4">
      <div id="navigation" aria-label="navigation">
        <details
          v-for="(items, parent) in navigationItems"
          :key="parent"
          :open="isParentActive(parent)"
          class="navigation-group"
          ref="details"
          @toggle="toggleDetails($event, parent)"
        >
          <summary class="navigation-parent font-weight-medium">
            <v-icon :icon="isDetailsOpen(parent) ? 'PhCaretDown' : 'PhCaretRight'" :size="14" class="indicator"/>
            <v-icon
              v-if="String(parent) === 'mandates'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhHandCoins"
            />
            <v-icon
              v-if="String(parent) === 'bank'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhArrowsLeftRight"
            />
            <v-icon
              v-if="String(parent) === 'usersManagement'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhUsers"
            />
            <v-icon
              v-if="String(parent) === 'fees'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhReceipt"
            />
            <v-icon
              v-if="String(parent) === 'support'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhHeadset"
            />
            <v-icon
              v-if="String(parent) === 'referral'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhGift"
            />
            <v-icon
              v-if="String(parent) === 'legal'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhScales"
            />
            <v-icon
              v-if="String(parent) === 'faq'"
              :size="20"
              class="navigation-parent-icon"
              icon="PhQuestion"
            />
            {{ t("components.navigationDrawer.items." + parent) }}
          </summary>
          <ul class="navigation-list">
            <li v-for="(item, key) in items" :key="key" class="navigation-item">
              <RouterLink
                :class="[
                  isActive(item) ? 'active font-weight-bold text-secondary' : 'text-inactive',
                  'navigation-link',
                  'v-list-item-subtitle'
                ]"
                :to="{ name: item.name }"
              >
                {{ t("components.navigationDrawer.items." + key) }}
              </RouterLink>
            </li>
          </ul>
        </details>
      </div>
    </div>

    <!-- Your Account -->
    <template #append>
      <v-divider />
      <v-list :border="false">
        <p class="v-list-item-subtitle px-4 py-4">
          {{ t('components.navigationDrawer.append.title') }}
        </p>

        <!-- Name -->
        <v-list-item :subtitle="userFullName" density="compact">
          <template #prepend>
            <div class="navigation-parent-icon d-flex align-items-center" style="margin-left: 2px;">
              <AvatarComponent :text="userFullName" />
            </div>
          </template>
        </v-list-item>

        <!-- Update profile button -->
        <v-list-item
          :subtitle="$t('components.navigationDrawer.append.profile')"
          :to="`/user/profile`"
          density="compact"
          link
        >
          <template #prepend>
            <div class="navigation-parent-icon d-flex align-items-center">
              <v-icon :size="24" icon="PhUserCircleGear"/>
            </div>
          </template>
        </v-list-item>

        <!-- Logout button -->
        <v-list-item :subtitle="t('components.navigationDrawer.append.logout')" density="compact" @click="logout">
          <template #prepend>
            <div class="navigation-parent-icon d-flex align-items-center">
              <v-icon :size="24" icon="PhPower"/>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<style scoped>
.v-navigation-drawer__prepend div {
  display: flex;
  align-items: center;
  height: 76px;
  padding: 16px;
}

.navigation-parent {
  display: flex;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
  outline: none;
  position: relative;
}

.navigation-parent::marker {
  display: none;
}

.indicator {
  margin-right: 10px;
  opacity: 0.7;
}

.navigation-parent-icon {
  margin-right: 4px;
}

.navigation-list {
  padding-left: 0;
  list-style: none;
}

.navigation-item {
  padding-block: 6px;
  padding-right: 4px;
  padding-left: 45px;
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 7px;
}

.navigation-item:has(.active) {
  background: #FBF6E6;
}

.navigation-item:has(.active)::before {
  content: '';
  position: absolute;
  left: 0;
  height: 70%;
  border-radius: 1000px;
  width: 4px;
  background: #d7a321;
}

.navigation-link {
  text-decoration: none;
}

details[open] summary ~ * {
  animation: v-list-expand 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes v-list-expand {
  0% {
    opacity: 0;
    transform: translateY(-8px) scaleY(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scaleY(1);
  }
}
</style>
